import React from "react";
import {
  FieldWrapper,
  FilterSelect,
  FilterToggle,
  FilterSortGroup,
  CompactDateInput,
} from "./jobsFilterUtils";

export const AUDIT_SORT_OPTIONS = [{ value: "created", label: "Created" }];

export const SUBJECT_TYPE_OPTIONS = [
  { label: "All", value: "" },
  { label: "Account", value: "account" },
  { label: "Field Staff", value: "fieldstaff" },
  { label: "Office Staff", value: "officestaff" },
  { label: "Candidate", value: "candidate" },
  { label: "Outsourced Staff", value: "outsourcedstaff" },
  { label: "Contact", value: "contact" },
  { label: "Client", value: "client" },
  { label: "Client Location", value: "client_location" },
  { label: "Client Account", value: "client_account" },
  { label: "Office", value: "office" },
  { label: "Shift", value: "shift" },
  { label: "Shift Actual", value: "shift_actual" },
  { label: "Week", value: "week" },
  { label: "Adjustment", value: "adjustment" },
  { label: "Payer", value: "payer" },
  { label: "Preference", value: "preference" },
  { label: "Employee Certification", value: "employee_cert" },
  { label: "Address", value: "address" },
  { label: "File", value: "file" },
  { label: "Tag", value: "tag" },
  { label: "Type", value: "type" },
  { label: "Status", value: "status" },
];

export const DETAIL_TYPE_OPTIONS = [...SUBJECT_TYPE_OPTIONS];

export const ACTION_OPTIONS = [
  { label: "All", value: "" },
  { label: "Create", value: "create" },
  { label: "Add", value: "add" },
  { label: "Change", value: "change" },
  { label: "Remove", value: "remove" },
  { label: "Delete", value: "delete" },
  { label: "Info", value: "info" },
];

export function AdminAuditsFilterFields({
  focusedFieldId,
  onFocusClear,
  subjectObjectClass,
  setSubjectObjectClass,
  auditMetaObjectClass,
  setAuditMetaObjectClass,
  filterAction,
  setFilterAction,
  filterDateFrom,
  setFilterDateFrom,
  filterDateTo,
  setFilterDateTo,
  showObjectId,
  setShowObjectId,
  sortBy = "created",
  sortDirection,
  setSortDirection,
  messageMode = false,
}) {
  return (
    <>
      <FieldWrapper
        fieldId="subjectObjectClass"
        label="Subject Type"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSelect
          id="admin-audits-subject-type"
          value={subjectObjectClass}
          onChange={setSubjectObjectClass}
          options={SUBJECT_TYPE_OPTIONS}
          placeholder={null}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="auditMetaObjectClass"
        label="Detail Type"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSelect
          id="admin-audits-detail-type"
          value={auditMetaObjectClass}
          onChange={setAuditMetaObjectClass}
          options={DETAIL_TYPE_OPTIONS}
          placeholder={null}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="filterAction"
        label="Action"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSelect
          id="admin-audits-action"
          value={filterAction}
          onChange={setFilterAction}
          options={ACTION_OPTIONS}
          placeholder={null}
        />
      </FieldWrapper>

      {!messageMode && (
        <>
          <FieldWrapper
            fieldId="filterDateFrom"
            label="Date From"
            focusedFieldId={focusedFieldId}
            onFocusClear={onFocusClear}
          >
            <CompactDateInput
              id="admin-audits-date-from"
              value={filterDateFrom}
              onChange={setFilterDateFrom}
            />
          </FieldWrapper>

          <FieldWrapper
            fieldId="filterDateTo"
            label="Date To"
            focusedFieldId={focusedFieldId}
            onFocusClear={onFocusClear}
          >
            <CompactDateInput
              id="admin-audits-date-to"
              value={filterDateTo}
              onChange={setFilterDateTo}
            />
          </FieldWrapper>
        </>
      )}

      <FieldWrapper
        fieldId="showObjectId"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterToggle
          id="admin-audits-show-object-id"
          label="Show Object ID"
          checked={showObjectId}
          onChange={setShowObjectId}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-audits-sort"
          sortBy={sortBy}
          sortOrder={sortDirection}
          onSortByChange={() => {}}
          onSortOrderChange={setSortDirection}
          sortOptions={AUDIT_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminAuditsFilterChips({
  subjectObjectClass,
  auditMetaObjectClass,
  filterAction,
  filterDateFrom,
  filterDateTo,
  showObjectId,
  sortDirection,
  messageMode = false,
}) {
  const chips = [];

  if (subjectObjectClass) {
    const label = SUBJECT_TYPE_OPTIONS.find((opt) => opt.value === subjectObjectClass)?.label || subjectObjectClass;
    chips.push({
      id: "subjectObjectClass",
      key: "subjectObjectClass",
      fieldId: "subjectObjectClass",
      label: `Subject: ${label}`,
    });
  }

  if (auditMetaObjectClass) {
    const label = DETAIL_TYPE_OPTIONS.find((opt) => opt.value === auditMetaObjectClass)?.label || auditMetaObjectClass;
    chips.push({
      id: "auditMetaObjectClass",
      key: "auditMetaObjectClass",
      fieldId: "auditMetaObjectClass",
      label: `Detail: ${label}`,
    });
  }

  if (filterAction) {
    const label = ACTION_OPTIONS.find((opt) => opt.value === filterAction)?.label || filterAction;
    chips.push({
      id: "filterAction",
      key: "filterAction",
      fieldId: "filterAction",
      label: `Action: ${label}`,
    });
  }

  if (!messageMode && filterDateFrom) {
    chips.push({
      id: "filterDateFrom",
      key: "filterDateFrom",
      fieldId: "filterDateFrom",
      label: `From: ${filterDateFrom}`,
    });
  }

  if (!messageMode && filterDateTo) {
    chips.push({
      id: "filterDateTo",
      key: "filterDateTo",
      fieldId: "filterDateTo",
      label: `To: ${filterDateTo}`,
    });
  }

  if (showObjectId) {
    chips.push({
      id: "showObjectId",
      key: "showObjectId",
      fieldId: "showObjectId",
      label: "Show Object ID",
    });
  }

  if (sortDirection !== "desc") {
    chips.push({
      id: "sortDirection",
      key: "sortDirection",
      fieldId: "sort",
      label: `Direction: ${sortDirection === "asc" ? "Ascending" : "Descending"}`,
    });
  }

  return chips;
}
